/**
 * VideoPresenter.tsx
 * Live control surface for an Interactive Video activity. Shown in the Video
 * tab while a session is live and the current slide holds a video_quiz config.
 * The teacher plays the video externally and taps "Ask" on each timestamped
 * question — that pushes it to students as a normal MC / Short Answer question,
 * so the existing student screens + LiveResults handle everything.
 */

import React, { useEffect, useState } from 'react';
import { Clock, Send, Check } from 'lucide-react';
import { clsx } from 'clsx';
import { useAppStore } from '@store/appStore';
import { loadSlideConfig, getStoredSlideId } from '@office/settingsService';
import { getCurrentSlideIndex } from '@office/slideService';
import { updateActiveSlide, updateRoomStatus } from '@db/roomService';
import LiveResults from './LiveResults';
import type { VideoQuizConfig, VideoQuestion, MCConfig, ShortAnswerConfig } from '@types-local/quiz.types';

function fmt(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export default function VideoPresenter(): React.ReactElement {
  const activeRoom = useAppStore((s) => s.activeRoom);
  const [config, setConfig] = useState<VideoQuizConfig | null>(null);
  const [slideId, setSlideId] = useState('');
  const [askedId, setAskedId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const idx = await getCurrentSlideIndex();
      const cfg = loadSlideConfig(idx);
      if (cancelled) return;
      if (cfg?.type === 'video_quiz') {
        setConfig(cfg);
        setSlideId(getStoredSlideId(idx) ?? `slide_${idx}`);
      } else {
        setConfig(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const ask = async (q: VideoQuestion) => {
    if (!activeRoom?.roomCode) return;
    setBusyId(q.id);
    try {
      const pushed: MCConfig | ShortAnswerConfig = q.kind === 'mc'
        ? {
            type: 'multiple_choice',
            question: q.question,
            options: q.options ?? [],
            timeLimit: null,
            competitionMode: false,
            starValue: q.starValue,
            shuffleOptions: false,
            showCorrectAnswerAfter: true,
            blooms: q.blooms ?? null,
          }
        : {
            type: 'short_answer',
            question: q.question,
            maxLength: 200,
            timeLimit: null,
            starValue: q.starValue,
          };
      await updateActiveSlide(activeRoom.roomCode, slideId, pushed);
      await updateRoomStatus(activeRoom.roomCode, 'active');
      setAskedId(q.id);
    } catch (err) {
      console.error('[GuroHub] Failed to ask video question:', err);
    } finally {
      setBusyId(null);
    }
  };

  if (!config) {
    return (
      <div className="p-6 text-center space-y-2">
        <div className="text-4xl">🎬</div>
        <p className="text-sm text-slate-700 font-medium">No video on this slide</p>
        <p className="text-xs text-slate-500">Go to the slide with your Interactive Video, then play it and tap questions here.</p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-xl bg-red-500/15 border border-red-500/30 flex items-center justify-center text-base">🎬</div>
        <div>
          <h2 className="text-sm font-bold text-slate-900">Interactive Video</h2>
          <p className="text-[10px] text-slate-500">Play the video, then tap a question when you reach its time.</p>
        </div>
      </div>

      <div className="space-y-2">
        {config.questions.map((q, i) => {
          const isAsked = askedId === q.id;
          return (
            <div key={q.id}
              className={clsx('rounded-xl border p-2.5', isAsked ? 'border-brand-500/50 bg-brand-500/5' : 'border-surface-border bg-surface-elevated')}>
              <div className="flex items-center gap-2">
                <span className="flex items-center gap-1 text-[10px] font-mono text-slate-500 tabular-nums">
                  <Clock size={11} /> {fmt(q.timestamp)}
                </span>
                <span className="text-[9px] uppercase tracking-wide text-slate-400">{q.kind === 'mc' ? 'MC' : 'Short'}</span>
                <div className="flex-1" />
                <button onClick={() => ask(q)} disabled={busyId === q.id}
                  className={clsx('flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-lg transition-colors',
                    isAsked ? 'bg-green-500/15 text-green-600' : 'bg-brand-500 text-white hover:bg-brand-600')}>
                  {isAsked ? <><Check size={12} /> Asked</> : <><Send size={12} /> Ask</>}
                </button>
              </div>
              <p className="text-xs text-slate-700 mt-1.5">{i + 1}. {q.question}</p>
            </div>
          );
        })}
      </div>

      {/* Live results for the currently-asked question (reuses the standard panel) */}
      {askedId && activeRoom?.activeConfig && (
        <div className="border-t border-surface-border pt-2">
          <LiveResults />
        </div>
      )}
    </div>
  );
}
